import { BreakpointObserver } from '@angular/cdk/layout';
import { EventEmitter, Injectable } from '@angular/core';
import { HeaderConfigService } from '@os-components';
import {BehaviorSubject, combineLatest, ReplaySubject} from 'rxjs';
import { filter } from 'rxjs/operators';
import { ScrollService } from '../../features/landing-page/services/scroll.service';
import { CustomBreakpoint } from '../../helpers/breakpoint.helper';
import { BlogPost } from '../../models/blog-posts.model';
import { BlogService } from '../blog/blog.service';

export interface MetaData {
    pageTitle: string;
    pageDescription: string;
}

@Injectable({
    providedIn: 'root',
})
export class ContentService {
    public initializedContent = new EventEmitter();

    public updatedContent = new EventEmitter();

    private reachedLastPageSubject = new BehaviorSubject<boolean>(false);

    private metaDataSubject = new ReplaySubject<MetaData>(1);

    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly metaData = this.metaDataSubject.asObservable();

    public get reachedLastPage() {
        return this.reachedLastPageSubject.asObservable();
    }

    /**
     * hero post on top of page
     */
    private heroPost?: BlogPost;

    /**
     * first static post
     */
    private firstPost?: BlogPost;

    /**
     * second static post
     */
    private secondPost?: BlogPost;

    /**
     * third static post
     */
    private thirdPost?: BlogPost;

    /**
     * All not boosted, already loaded blog posts
     */
    private blogPosts: BlogPost[] = [];

    /**
     * all boosted blog posts
     */
    private boostedBlogPosts: BlogPost[] = [];

    /**
     * page count of blog posts. Increased automatically when user loads more posts
     */
    private pageCount = 1;

    private pageSize = 24;

    /**
     * row 3 -> load 3 articles
     * row 2 -> load 2 articles
     */
    private row = -1;

    private breakpoint?: CustomBreakpoint;

    /**
     * Determines if blog posts are loaded
     */
    private postsDone = false;
    /**
     * Determines if boosted posts are loaded
     */
    private layoutPostsDone = false;
    /**
     * Determines if hero posts are loaded
     */
    private heroDone = false;

    /**
     * On each click at "Lade Artikel" there will be 24 new articles loaded from the backend. Only 6 articles are displayed immediately.
     * The other 18 articles are stored in this variable and will be displayed in 6er steps by scrolling
     */
    private storedPost: BlogPost[] = [];

    /**
     * determines if all blog articles are loaded from the server.
     */
    private isLastPage = false;

    /**
     * preview id for hero posts and boosted posts
     */
    private layoutPreviewId = '';

    public constructor(
        private blog: BlogService,
        private scrollService: ScrollService,
        private config: HeaderConfigService,
        private breakpointObserver: BreakpointObserver
    ) {
        // infity srolling triggered
        this.scrollService.triggeredScroll.subscribe(() => {
            if (this.storedPost.length) {
                this.blogPosts = [...this.blogPosts, ...this.storedPost.slice(0, this.row)];
                this.storedPost = this.storedPost.slice(this.row, this.storedPost.length);
                this.updateRow();
                this.updatedContent.emit();
            }
        });

        combineLatest([this.config.language, this.config.countryShort])
            .pipe(filter(([language, countryShort]) => language.length > 0 && countryShort.length > 0))
            .subscribe(() => {
                this.initBlogPosts();
            });

        this.initBreakpointListener();
        this.row = this.breakpoint || -1;
    }

    /**
     * Get the hero blog post of the landing page, the first 3 posts before the subscribe form and the first iteration of the blog posts
     */
    public initBlogPosts(): void {
        this.pageCount = 1;
        this.blog.getBlogContent(1, this.pageSize).subscribe((response: BlogPost[]) => {
            if (response) {
                this.firstPost = response[0];
                this.secondPost = response[1];
                this.thirdPost = response[2];
                this.blogPosts = response.slice(3, 5);
                this.storedPost = response.slice(5);
                this.checkLastPageReached();
                this.postsDone = true;
                if (this.checkAllLoaded()) {
                    this.initializedContent.emit();
                }
            }
        });

        this.blog.getBlogLayoutPosts().subscribe((response) => {
            if (response.length) {
                const blogLayout = response[0];
                if (blogLayout.pageTitle && blogLayout.pageDescription) {
                    this.metaDataSubject.next({
                        pageTitle: blogLayout.pageTitle,
                        pageDescription: blogLayout.pageDescription,
                    });
                }
                this.layoutPreviewId = blogLayout.preview_id;

                // get hero post
                this.blog.getPostById(blogLayout.hero.page_id).subscribe((resp) => {
                    if (resp) {
                        this.heroPost = resp;
                    }
                    this.heroDone = true;
                    if (this.checkAllLoaded()) {
                        this.initializedContent.emit();
                    }
                });

                // get boosted posts
                this.blog.getPostsById(blogLayout.boosted).subscribe((resp) => {
                    if (resp.length) {
                        this.boostedBlogPosts = resp;
                        // Sort boosted posts based on the order of our page_ids
                        this.boostedBlogPosts.sort(
                            (a, b) =>
                                blogLayout.boosted.indexOf(a.page_id) - blogLayout.boosted.indexOf(b.page_id)
                        );
                    } else {
                        this.boostedBlogPosts = [];
                    }
                    this.layoutPostsDone = true;
                    if (this.checkAllLoaded()) {
                        this.initializedContent.emit();
                    }
                });
            } else {
                this.boostedBlogPosts = [];
                this.layoutPostsDone = true;
                this.heroDone = true;
                if (this.checkAllLoaded()) {
                    this.initializedContent.emit();
                }
            }
        });
    }

    public getNextIteration(): void {
        this.pageCount++;
        this.blog.getBlogContent(this.pageCount, this.pageSize).subscribe((response: BlogPost[]) => {
            this.blogPosts = [...this.blogPosts, ...response.slice(0, 5)];
            this.storedPost = response.slice(5, response.length);
            this.checkLastPageReached();
            this.updatedContent.emit();
        });
    }

    public getHeroPost(): BlogPost | undefined {
        return this.heroPost;
    }

    public getFirstPost(): BlogPost | undefined {
        return this.firstPost;
    }

    public getSecondPost(): BlogPost | undefined {
        return this.secondPost;
    }

    public getThirdPost(): BlogPost | undefined {
        return this.thirdPost;
    }

    public getBlogPosts(): BlogPost[] {
        return this.blogPosts;
    }

    public getBoostedPosts(): BlogPost[] {
        return this.boostedBlogPosts;
    }

    public getIsLastPage(): boolean {
        return this.isLastPage;
    }

    public getLayoutPreviewId(): string {
        return this.layoutPreviewId;
    }

    private checkAllLoaded(): boolean {
        if (this.heroDone && this.postsDone && this.layoutPostsDone) {
            this.postsDone = false;
            this.layoutPostsDone = false;
            this.heroDone = false;

            return true;
        }

        return false;
    }

    /**
     * checks if the last page of the database was reached
     */
    private checkLastPageReached(): void {
        if (this.blog.getTotalPostPages() === this.pageCount) {
            this.isLastPage = true;
            this.reachedLastPageSubject.next(true);
        }
    }

    private updateRow(): void {
        if (this.breakpoint === 3) {
            // computer
            if (this.row === 3) {
                this.row = 2;
            } else {
                this.row = 3;
            }
        } else if (this.breakpoint === 2) {
            // tablet
            this.row = 2;
        } else {
            // mobile
            this.row = 1;
        }
    }

    private initBreakpointListener(): void {
        this.breakpointObserver
            .observe(['(max-width: 1024px)', '(max-width: 1200px)', '(min-width: 1201px)'])
            .subscribe((state) => {
                const breakpoints = state.breakpoints;
                if (breakpoints['(max-width: 1024px)']) {
                    this.breakpoint = 1;
                } else if (breakpoints['(max-width: 1200px)']) {
                    this.breakpoint = 2;
                } else {
                    this.breakpoint = 3;
                }
            });
    }

    /*     private setRow(): void {
        if (this.breakpoint && this.breakpoint >= 4) {
            // computer
            this.row = 3;
        } else if (this.breakpoint === 1 || this.breakpoint === 2) {
            // tablet
            this.row = 2;
        } else {
            // mobile
            this.row = 1;
        }
    } */
}
